import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Users, UserPlus, BarChart3, Loader2, Zap, Package, Eye,
  Shield, Trash2, ChevronDown, Crown, Webhook, Copy, RefreshCw, Check, AlertTriangle
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { ErrorBanner } from '../components/ErrorBanner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const pathNames = {
  path_fast_cash: { name: 'Fast Cash', icon: Zap, color: '#f59e0b' },
  path_digital_product: { name: 'Digital Product', icon: Package, color: '#8b5cf6' },
  path_faceless_affiliate: { name: 'Faceless Affiliate', icon: Eye, color: '#10b981' },
};

const planColors = {
  free: 'bg-gray-100 text-gray-600',
  basic: 'bg-teal-50 text-teal-700',
  pro: 'bg-amber-50 text-amber-700',
};

const Admin = () => {
  const { user, isAdmin, getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [addEmail, setAddEmail] = useState('');
  const [addName, setAddName] = useState('');
  const [addRole, setAddRole] = useState('member');
  const [addPlan, setAddPlan] = useState('free');
  const [adding, setAdding] = useState(false);
  const [integrations, setIntegrations] = useState(null);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const webhookFullUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/new-user`;

  const fetchIntegrations = useCallback(async () => {
    setIntegrationsLoading(true);
    try {
      const res = await fetch(`${API}/admin/integrations`, { headers: getAuthHeaders() });
      if (res.ok) {
        setIntegrations(await res.json());
      } else {
        toast.error('Failed to load integration settings');
      }
    } catch (err) {
      toast.error('Failed to load integration settings');
    } finally {
      setIntegrationsLoading(false);
    }
  }, [getAuthHeaders]);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedField(null), 1800);
    }).catch(() => toast.error('Copy failed'));
  };

  const handleRotateSecret = async () => {
    if (!window.confirm(
      'Rotate the webhook secret?\n\nYour current secret will keep working for 24 hours so you can update Zapier. After that, only the new secret will work.'
    )) return;
    setRotating(true);
    try {
      const res = await fetch(`${API}/admin/integrations/rotate-secret`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setIntegrations(data);
        toast.success('Secret rotated. Old secret valid for 24 hours.');
      } else {
        toast.error('Failed to rotate secret');
      }
    } catch (err) {
      toast.error('Failed to rotate secret');
    } finally {
      setRotating(false);
    }
  };

  const fetchAdminData = useCallback(async () => {
    setError(null);
    try {
      const authHeaders = getAuthHeaders();
      const [statsRes, usersRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { headers: authHeaders }),
        fetch(`${API}/admin/users`, { headers: authHeaders })
      ]);

      if (statsRes.status === 401 || usersRes.status === 401) {
        setError({ message: 'Your session has expired. Please sign in again.', isAuth: true });
        return;
      }

      if (statsRes.status === 403 || usersRes.status === 403) {
        setError({ message: 'You do not have admin access.' });
        return;
      }

      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
    } catch (err) {
      process.env.NODE_ENV === "development" && console.error('Error fetching admin data:', err);
      setError({ message: 'Could not connect to the server. Check your connection and try again.' });
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (isAdmin) {
      setLoading(true);
      fetchAdminData();
    }
  }, [isAdmin, fetchAdminData]);

  const handleInviteAdmin = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const response = await fetch(`${API}/admin/invite`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail, invited_by_user_id: user.id })
      });
      if (response.ok) {
        toast.success('Admin invite sent!');
        setInviteEmail('');
      } else {
        toast.error('Failed to send invite');
      }
    } catch (err) {
      toast.error('Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!addEmail.trim()) return;
    setAdding(true);
    try {
      const params = new URLSearchParams({
        email: addEmail.trim(),
        name: addName.trim(),
        role: addRole,
        plan_id: addPlan
      });
      const response = await fetch(`${API}/admin/users/add?${params}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const planNames = { free: 'Free', basic: 'Basic', pro: 'Pro' };
        toast.success(`${addEmail.trim()} added with ${planNames[addPlan]} plan`);
        setAddEmail('');
        setAddName('');
        setAddRole('member');
        setAddPlan('free');
        fetchAdminData();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to add user');
      }
    } catch (err) {
      toast.error('Failed to add user');
    } finally {
      setAdding(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    setActionLoading(`role-${userId}`);
    try {
      const response = await fetch(`${API}/admin/users/${userId}/role?role=${newRole}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success(`Role updated to ${newRole}`);
        setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, role: newRole } : u));
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update role');
      }
    } catch (err) {
      toast.error('Failed to update role');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdatePlan = async (userId, newPlan) => {
    setActionLoading(`plan-${userId}`);
    try {
      const response = await fetch(`${API}/admin/users/${userId}/plan?plan_id=${newPlan}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const planNames = { free: 'Free', basic: 'Basic', pro: 'Pro' };
        toast.success(`Plan updated to ${planNames[newPlan]}`);
        setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, plan_id: newPlan, plan_name: planNames[newPlan] } : u));
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update plan');
      }
    } catch (err) {
      toast.error('Failed to update plan');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${email}? This removes all their data and cannot be undone.`)) {
      return;
    }
    setActionLoading(`delete-${userId}`);
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success(`${email} has been removed`);
        setUsers(prev => prev.filter(u => u.user_id !== userId));
        setExpandedUser(null);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to delete user');
      }
    } catch (err) {
      toast.error('Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  if (!isAdmin) {
    return (
      <AppLayout maxWidth="max-w-6xl">
        <div className="text-center py-16">
          <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-bold">Admin Access Required</h2>
        </div>
      </AppLayout>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <AppLayout maxWidth="max-w-6xl">
        <ErrorBanner
          message={error.message}
          isAuthError={error.isAuth}
          onRetry={!error.isAuth ? () => { setLoading(true); fetchAdminData(); } : undefined}
        />
      </AppLayout>
    );
  }

  return (
    <AppLayout maxWidth="max-w-6xl">
      <Toaster position="top-center" />
      <h1 className="text-2xl font-bold text-foreground mb-6" data-testid="admin-title">Admin Dashboard</h1>

      <Tabs defaultValue="users" className="w-full" onValueChange={(v) => { if (v === 'integrations' && !integrations) fetchIntegrations(); }}>
        <TabsList className="mb-6 bg-transparent border-b border-border/60 rounded-none h-auto p-0 gap-1" data-testid="admin-tabs">
          <TabsTrigger
            value="users"
            data-testid="tab-users"
            className="rounded-none rounded-t-md border-b-2 border-transparent px-4 py-2 data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700 data-[state=active]:border-teal-600 data-[state=active]:shadow-none transition-all"
          >
            Users &amp; Stats
          </TabsTrigger>
          <TabsTrigger
            value="integrations"
            data-testid="tab-integrations"
            className="rounded-none rounded-t-md border-b-2 border-transparent px-4 py-2 data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700 data-[state=active]:border-teal-600 data-[state=active]:shadow-none transition-all"
          >
            <Webhook className="h-3.5 w-3.5 mr-1.5" />
            Integrations
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users">
      {/* Stats Row */}
      {stats && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6"
        >
          <Card className="border-border border-t-4 border-t-teal-500 creataby-card-hover" data-testid="stat-total-users">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <Users className="h-3.5 w-3.5" />
                Total Users
              </div>
              <p className="text-3xl font-bold">{stats.total_users}</p>
              <p className="text-xs text-muted-foreground">{stats.recent_signups_7d} new this week</p>
            </CardContent>
          </Card>

          <Card className="border-border border-t-4 border-t-amber-500 creataby-card-hover" data-testid="stat-onboarded">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <Users className="h-3.5 w-3.5" />
                Onboarded
              </div>
              <p className="text-3xl font-bold">{stats.onboarded_users}</p>
              <p className="text-xs text-muted-foreground">{stats.total_users > 0 ? Math.round((stats.onboarded_users / stats.total_users) * 100) : 0}% conversion</p>
            </CardContent>
          </Card>

          <Card className="border-border border-t-4 border-t-indigo-500 creataby-card-hover" data-testid="stat-outputs">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <BarChart3 className="h-3.5 w-3.5" />
                Creataby Generations
              </div>
              <p className="text-3xl font-bold">{stats.total_ai_outputs}</p>
              <p className="text-xs text-muted-foreground">{stats.total_generations_this_month} this month</p>
            </CardContent>
          </Card>

          <Card className="border-border border-t-4 border-t-emerald-500 creataby-card-hover" data-testid="stat-completions">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <BarChart3 className="h-3.5 w-3.5" />
                Steps Completed
              </div>
              <p className="text-3xl font-bold">{stats.total_step_completions}</p>
              <p className="text-xs text-muted-foreground">{stats.saved_outputs} outputs saved</p>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Subscription + Path Breakdown */}
      {stats && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-6"
        >
          <Card className="border-border" data-testid="stat-subscriptions">
            <CardContent className="pt-4 pb-3">
              <div className="text-muted-foreground text-xs mb-2 font-medium">Subscriptions</div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Free</span>
                  <span className="font-medium">{stats.subscription_breakdown?.free || 0}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-teal-400">Basic ($27/mo)</span>
                  <span className="font-medium">{stats.subscription_breakdown?.basic || 0}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-emerald-400">Pro ($97/mo)</span>
                  <span className="font-medium">{stats.subscription_breakdown?.pro || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border" data-testid="stat-paths">
            <CardContent className="pt-4 pb-3">
              <div className="text-muted-foreground text-xs mb-2 font-medium">Path Distribution</div>
              <div className="space-y-2">
                {Object.entries(stats.path_distribution).map(([pathId, count]) => {
                  const path = pathNames[pathId];
                  if (!path) return null;
                  return (
                    <div key={pathId} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5">
                        <path.icon className="h-3.5 w-3.5" style={{ color: path.color }} />
                        {path.name}
                      </span>
                      <span className="font-medium">{count}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Add User + Invite Admin */}
        <div className="space-y-4">
          {/* Add User */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="border-border" data-testid="add-user-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <UserPlus className="h-4 w-4" />
                  Add User
                </CardTitle>
                <CardDescription className="text-sm">Pre-create a user with plan and role</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddUser} className="space-y-3">
                  <div>
                    <Label htmlFor="addEmail" className="text-sm">Email</Label>
                    <Input
                      id="addEmail"
                      type="email"
                      placeholder="user@example.com"
                      value={addEmail}
                      onChange={(e) => setAddEmail(e.target.value)}
                      className="mt-1 h-9"
                      required
                      data-testid="add-user-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="addName" className="text-sm">Name (optional)</Label>
                    <Input
                      id="addName"
                      type="text"
                      placeholder="Neal Hollis"
                      value={addName}
                      onChange={(e) => setAddName(e.target.value)}
                      className="mt-1 h-9"
                      data-testid="add-user-name"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Role</Label>
                    <div className="flex gap-2 mt-1">
                      <Button type="button" size="sm" variant={addRole === 'member' ? 'default' : 'outline'} className="flex-1 h-8 text-xs" onClick={() => setAddRole('member')}>Member</Button>
                      <Button type="button" size="sm" variant={addRole === 'admin' ? 'default' : 'outline'} className="flex-1 h-8 text-xs" onClick={() => setAddRole('admin')}><Shield className="h-3 w-3 mr-1" />Admin</Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Plan</Label>
                    <div className="flex gap-2 mt-1">
                      {['free', 'basic', 'pro'].map((p) => (
                        <Button key={p} type="button" size="sm" variant={addPlan === p ? 'default' : 'outline'} className="flex-1 h-8 text-xs capitalize" onClick={() => setAddPlan(p)}>
                          {p === 'pro' && <Crown className="h-3 w-3 mr-1" />}{p}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    disabled={!addEmail.trim() || adding}
                    className="w-full"
                    size="sm"
                    data-testid="add-user-submit"
                  >
                    {adding ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Adding...</>
                    ) : (
                      <><UserPlus className="mr-2 h-4 w-4" />Add User &amp; Send Invite</>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Invite Admin (quick) */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}>
            <Card className="border-border" data-testid="invite-admin-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Shield className="h-4 w-4" />
                  Quick Admin Invite
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleInviteAdmin} className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="admin@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="h-9 flex-1"
                    data-testid="invite-email-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={!inviteEmail.trim() || inviting}
                    size="sm"
                    className="h-9"
                    data-testid="invite-submit-btn"
                  >
                    {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Users List with Management */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="lg:col-span-2"
        >
          <Card className="border-border" data-testid="users-list-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-4 w-4" />
                User Management
              </CardTitle>
              <CardDescription className="text-sm">{users.length} total users — click to manage</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {users.map((u) => (
                  <div key={u.user_id} className="border border-border rounded-lg overflow-hidden">
                    {/* User row */}
                    <button
                      onClick={() => setExpandedUser(expandedUser === u.user_id ? null : u.user_id)}
                      className="w-full flex items-center justify-between p-3 hover:bg-secondary/40 transition-colors text-left"
                      data-testid={`user-row-${u.email}`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <img 
                          src={u.avatar_url || `https://api.dicebear.com/9.x/notionists/svg?seed=${u.email}&backgroundColor=f5f5f5`}
                          alt=""
                          className="w-8 h-8 rounded-full flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-foreground truncate">
                            {u.display_name || u.email}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {u.role === 'admin' && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 font-medium">
                            <Shield className="h-3 w-3 inline mr-0.5" />Admin
                          </span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${planColors[u.plan_id] || planColors.free}`}>
                          {u.plan_id === 'pro' && <Crown className="h-3 w-3 inline mr-0.5" />}
                          {(u.plan_name || 'Free')}
                        </span>
                        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${expandedUser === u.user_id ? 'rotate-180' : ''}`} />
                      </div>
                    </button>

                    {/* Expanded management panel */}
                    {expandedUser === u.user_id && (
                      <div className="border-t border-border bg-secondary/20 p-3 space-y-3" data-testid={`user-panel-${u.email}`}>
                        {/* Role control */}
                        <div>
                          <Label className="text-xs font-medium text-muted-foreground mb-1.5 block">Role</Label>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant={u.role === 'member' ? 'default' : 'outline'}
                              className="h-8 text-xs flex-1"
                              disabled={actionLoading === `role-${u.user_id}` || u.user_id === user.id}
                              onClick={() => handleUpdateRole(u.user_id, 'member')}
                              data-testid={`role-member-${u.email}`}
                            >
                              Member
                            </Button>
                            <Button
                              size="sm"
                              variant={u.role === 'admin' ? 'default' : 'outline'}
                              className="h-8 text-xs flex-1"
                              disabled={actionLoading === `role-${u.user_id}` || u.user_id === user.id}
                              onClick={() => handleUpdateRole(u.user_id, 'admin')}
                              data-testid={`role-admin-${u.email}`}
                            >
                              <Shield className="h-3 w-3 mr-1" />Admin
                            </Button>
                          </div>
                        </div>

                        {/* Plan control */}
                        <div>
                          <Label className="text-xs font-medium text-muted-foreground mb-1.5 block">Membership Plan</Label>
                          <div className="flex gap-2">
                            {['free', 'basic', 'pro'].map((plan) => (
                              <Button
                                key={plan}
                                size="sm"
                                variant={(u.plan_id || 'free') === plan ? 'default' : 'outline'}
                                className="h-8 text-xs flex-1 capitalize"
                                disabled={actionLoading === `plan-${u.user_id}`}
                                onClick={() => handleUpdatePlan(u.user_id, plan)}
                                data-testid={`plan-${plan}-${u.email}`}
                              >
                                {actionLoading === `plan-${u.user_id}` ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  plan === 'pro' ? <><Crown className="h-3 w-3 mr-1" />{plan}</> : plan
                                )}
                              </Button>
                            ))}
                          </div>
                        </div>

                        {/* User info */}
                        <div className="text-xs text-muted-foreground space-y-0.5">
                          {u.assigned_path_id && pathNames[u.assigned_path_id] && (
                            <p className="flex items-center gap-1">
                              {React.createElement(pathNames[u.assigned_path_id].icon, {
                                className: 'h-3 w-3',
                                style: { color: pathNames[u.assigned_path_id].color }
                              })}
                              {pathNames[u.assigned_path_id].name} — Step {u.current_step_number}
                            </p>
                          )}
                          {u.created_at && <p>Joined: {new Date(u.created_at).toLocaleDateString()}</p>}
                          {u.expires_at && <p>Expires: {new Date(u.expires_at).toLocaleDateString()}</p>}
                        </div>

                        {/* Delete */}
                        {u.user_id !== user.id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-xs text-destructive hover:text-destructive hover:bg-destructive/10 w-full"
                            disabled={actionLoading === `delete-${u.user_id}`}
                            onClick={() => handleDeleteUser(u.user_id, u.email)}
                            data-testid={`delete-${u.email}`}
                          >
                            {actionLoading === `delete-${u.user_id}` ? (
                              <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            ) : (
                              <Trash2 className="h-3 w-3 mr-1" />
                            )}
                            Remove User
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
        </TabsContent>

        <TabsContent value="integrations">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4 max-w-3xl"
            data-testid="integrations-panel"
          >
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Webhook className="h-4 w-4" />
                  New User Webhook (Zapier / ClickFunnels)
                </CardTitle>
                <CardDescription className="text-sm">
                  POST a new buyer to this endpoint after a successful sale to auto-provision their account and assign their plan.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {integrationsLoading || !integrations ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading integration settings...
                  </div>
                ) : (
                  <>
                    {/* Endpoint URL */}
                    <div>
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">Endpoint URL</Label>
                      <div className="flex gap-2 mt-1.5">
                        <Input
                          readOnly
                          value={webhookFullUrl}
                          className="font-mono text-xs"
                          data-testid="webhook-url-input"
                          onFocus={(e) => e.target.select()}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(webhookFullUrl, 'url')}
                          data-testid="copy-webhook-url"
                        >
                          {copiedField === 'url' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1">Method: <span className="font-mono">POST</span> · Content-Type: <span className="font-mono">application/json</span></p>
                    </div>

                    {/* Active Secret */}
                    <div>
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">Active API Secret</Label>
                      <div className="flex gap-2 mt-1.5">
                        <Input
                          readOnly
                          value={integrations.secret || ''}
                          className="font-mono text-xs"
                          data-testid="webhook-secret-input"
                          onFocus={(e) => e.target.select()}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(integrations.secret, 'secret')}
                          data-testid="copy-webhook-secret"
                        >
                          {copiedField === 'secret' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    </div>

                    {/* Previous secret (grace) */}
                    {integrations.previous_secret && integrations.previous_secret_expires_at && (
                      <div className="border border-amber-200 bg-amber-50 rounded-md p-3">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 text-xs">
                            <p className="font-medium text-amber-900">Previous secret still valid (24h grace)</p>
                            <p className="text-amber-800 mt-0.5">
                              Expires {new Date(integrations.previous_secret_expires_at).toLocaleString()}.
                              Update Zapier with the new secret above before this expires.
                            </p>
                            <code className="block mt-2 text-[11px] bg-white border border-amber-200 rounded px-2 py-1 font-mono break-all">
                              {integrations.previous_secret}
                            </code>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Sample payload */}
                    <div>
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">Request body (JSON)</Label>
                      <pre className="mt-1.5 text-[11px] bg-muted border border-border rounded-md p-3 font-mono overflow-x-auto" data-testid="webhook-sample-payload">
{`{
  "api_secret": "${integrations.secret}",
  "email": "{{customer_email}}",
  "name": "{{customer_name}}",
  "plan_id": "pro"
}`}
                      </pre>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Use <span className="font-mono">"plan_id": "basic"</span> for $27 buyers or <span className="font-mono">"pro"</span> for $97 buyers.
                      </p>
                    </div>

                    {/* Rotate */}
                    <div className="border-t border-border pt-4 flex items-center justify-between gap-4">
                      <div className="text-xs text-muted-foreground">
                        {integrations.rotated_at ? (
                          <>Last rotated: {new Date(integrations.rotated_at).toLocaleString()}</>
                        ) : (
                          <>Secret has never been rotated.</>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRotateSecret}
                        disabled={rotating}
                        data-testid="rotate-secret-btn"
                      >
                        {rotating ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                        )}
                        Rotate Secret
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
};

export default Admin;
